import {
  Color,
  MeshStandardMaterial,
  type WebGLProgramParametersWithUniforms,
} from "three";

/**
 * This is shader code exported from the Black Hole material
 * built with the Babylon node material editor.
 *
 * The uniforms were initially set using the same defaults as in the editor
 * and then adjusted as needed.
 */
export const getBlackHoleMaterial = (
  onBeforeCompile?: (shader: WebGLProgramParametersWithUniforms) => void,
) => {
  const material = new MeshStandardMaterial();

  material.onBeforeCompile = (shader) => {
    shader.uniforms.u_Color = { value: new Color("#A94AFF") };
    // how fast the animation runs
    shader.uniforms.u_TimeMultiplier = { value: 6.0 };
    // how close to the edge the effect reaches
    shader.uniforms.u_Float = { value: 0.1 };
    // how close to the center the effect reaches
    shader.uniforms.u_Float1 = { value: 0.4 };
    // the blur of the fresnel effect
    shader.uniforms.u_power = { value: 5.0 };
    shader.uniforms.iGlobalTime = { value: 0.0 };

    const vertCommon = `#include <common>`;
    shader.vertexShader = shader.vertexShader.replace(
      vertCommon,
      `
        ${vertCommon}
        varying vec3 vFresnelDir;
        varying vec3 vWorldPos;
      `,
    );

    const projectVertex = `#include <project_vertex>`;
    shader.vertexShader = shader.vertexShader.replace(
      projectVertex,
      `
        ${projectVertex}
        vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;

        mat3 model3x3 = mat3(modelMatrix);
        vFresnelDir = normalize(model3x3 * transformed);
      `,
    );

    const fragCommon = `#include <common>`;
    shader.fragmentShader = shader.fragmentShader.replace(
      fragCommon,
      `
        ${fragCommon}
        uniform vec3 u_Color;
        uniform float u_TimeMultiplier;
        uniform float iGlobalTime;
        uniform float u_Float;
        uniform float u_Float1;
        uniform float u_power;

        varying vec3 vFresnelDir;
        varying vec3 vWorldPos;
      `,
    );

    const outgoingLight =
      "vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;";
    shader.fragmentShader = shader.fragmentShader.replace(
      outgoingLight,
      `
        ${outgoingLight};
        vec3 viewDirectionWS = normalize(cameraPosition - vWorldPos);
        vec3 worldNormalLike = normalize(vFresnelDir);

        float t = u_TimeMultiplier * iGlobalTime;
        float n = sin(t);
        float bias = n * u_Float + u_Float1;

        float fresnel = pow(bias + abs(dot(viewDirectionWS, worldNormalLike)), u_power);
        fresnel = clamp(fresnel, 0.0, 1.0);

        float oneMinus = 1.0 - fresnel;
        outgoingLight = u_Color * vec3(oneMinus);
      `,
    );

    onBeforeCompile?.(shader);
  };

  return material;
};
